import { Button } from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { VictoryAxis, VictoryBar, VictoryChart, VictoryTooltip } from "victory";
import DateRange from "../../shared/components/DateRange";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { acumulateObject } from "../../shared/helper/validations";
import { get } from "../../shared/http/httpService";
import ChartTheme from "../theme/theme";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { defaultAgent } from "../../shared/helper/agentsHelper";
import ChannelSelect from "../../shared/components/ChannelSelect";
import { useSelector } from "react-redux";

const useStyles = styled(() => ({
  container: {
    display: "flex",
    height: "100vh",
  },
  zIndex0: {
    zIndex: "0",
  },
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
  },
  button: {
    backgroundColor: "#D9D9D9",
    color: "black",
    height: "40px",
    width: "100px",
    borderRadius: "5px",
    marginRight: "10px",
    marginLeft: "10px",
  },
}));
const ConversationsPerDay = () => {
  const classes = useStyles();
  let chartTheme = ChartTheme();
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const [valid, setValid] = useState(true);

  // eslint-disable-next-line no-unused-vars
  const [data, setData] = useState([]);
  const [dataGraph, setDataGraph] = useState([]);
  const [startDate, setStartDate] = useState(
    new Date(new Date().setHours(0, 0, 0, 0)),
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().setHours(0, 0, 0, 0)),
  );
  const [channelsSelected, setChannelsSelected] = useState([]);
  const [total, setTotal] = useState(0);
  const [fileName, setFileName] = useState(
    t("analytics:conversationsPerDayFile") +
      getParsedDate(startDate) +
      t("analytics:to") +
      getParsedDate(endDate),
  );
  const prepareDataForExport = (tableData) => {
    const csvConfig = mkConfig({
      fieldSeparator: ",",
      decimalSeparator: ".",
      useKeysAsHeaders: true,
      filename: fileName,
    });
    const csvData = tableData.map((item) => {
      let channel;
      switch (item.z) {
        case "MESSENGER":
          channel = "FACEBOOK";
          break;
        case "INSTAGRAMDIRECT":
          channel = "INSTAGRAM";
          break;
        case "msteams":
          channel = "TEAMS";
          break;
        case "WHATSAPP":
          channel = "WHATSAPP";
          break;
        case "WEBCHAT":
          channel = "WEBCHAT";
          break;
        default:
          channel = "WEB";
          break;
      }
      return {
        [t("analytics:date")]: item.x,
        [t("analytics:count")]: item.y,
        [t("conversationPerDay:channel")]: channel,
      };
    });
    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig, fileName)(csv);
  };
  const [accountSelected, setAccountSelected] = useState("");
  const fetchDataConversation = async () => {
    try {
      const isDefaultRange = 
                  dayjs(startDate).isSame(dayjs().startOf("day")) && 
                  dayjs(endDate).isSame(dayjs().startOf("day"));
                
      const queryEndDate = isDefaultRange 
        ? dayjs(endDate).add(1, "day") 
        : dayjs(endDate);

      const timezone = startDate.format("Z");
      const conversationPerDayTmp = await get("/report/conversationsPerDay", {
        startDate: dayjs(startDate).toDate(),
        endDate: queryEndDate.toDate(),
        channelsSelected: channelsSelected,
        timezone,
        accountSelected,
      });

      const newData = conversationPerDayTmp.map((conversation) => {
        return {
          x: dayjs(conversation.date).format("DD/MM/YYYY"),
          y: conversation.count,
          z: conversation.channel,
          label: `${conversation.count} convesation/s on the day: ${conversation.date}`,
        };
      });
      const dataGraph = acumulateObject(newData, "x", "y");
      setDataGraph(dataGraph);

      const total = newData.reduce((accumulator, object) => {
        return accumulator + object.y;
      }, 0);
      setTotal(total);
      setData(newData);
    } catch (error) {
      if (error.status === 202) {
        // Query is in progress, display a snackbar message
        enqueueSnackbar(t("analytics:timeoutHandlingReport"), {
          variant: "error",
        });
      } else {
        // Handle other errors
        console.error("Error fetching conversation per day:", error);
        throw error;
      }
    }
  };
  useEffect(() => {
    getAccountNames();
  }, []);

  const getAccountNames = () => {
    get("/essentials/data/agents")
      .then((data) => {
        let default_agent = defaultAgent(data.agents);
        setAccountSelected(default_agent.name);
      })
      .catch(() => {
        enqueueSnackbar(t("pages:errorGettingAccountNames"), {
          variant: "error",
        });
      });
  };

  const client = useSelector((state) => state.client);

  useEffect(() => {
    setData([]);
    setDataGraph([]);
    setStartDate(dayjs().startOf("day"));
    setEndDate(dayjs().startOf("day"));
  }, [client.name]);

  function handleDate(dates) {
    if (dates.startDate !== null && dates.endDate !== null && dates.valid) {
      setValid(dates.valid);
      setStartDate(dates.startDate);
      let newEndDate = new Date(dates.endDate);

      setEndDate(newEndDate);

      setFileName(
        t("analytics:conversationsPerDayFile") +
          t("analytics:from") +
          getParsedDate(dates.startDate) +
          t("analytics:to") +
          getParsedDate(newEndDate),
      );
    } else {
      setValid(dates.valid);
    }
  }

  function getParsedDate(date) {
    return dayjs(date).format("DD-MM-YYYY");
  }

  return (
    <Container maxwidth="lg">
      <Paper
        style={{
          marginBottom: "15px",
          paddingTop: "10px",
          paddingBottom: "15px",
          paddingLeft: "5px",
        }}>
        <Typography variant="h4">
          {t("analytics:conversationPerDay")}
        </Typography>
        <br></br>
        <Grid container justify="space-around" alignItems="center">
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onDateSelected={handleDate}
          />
          <ChannelSelect setChannels={setChannelsSelected} />
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginLeft: "10px",
            }}>
            <Button
              id="conversations-fetch"
              className={classes.button}
              variant="contained"
              color="default"
              size="medium"
              disabled={!valid || channelsSelected.length === 0}
              sx={{
                height: "40px",
                width: "130px",
                borderRadius: "5px",
              }}
              onClick={fetchDataConversation}>
              {t("conversationPerDay:fetch")}
            </Button>
            <Button
              id="conversations-download"
              className={classes.button}
              variant="contained"
              color="default"
              disabled={data.length === 0 || channelsSelected.length === 0}
              onClick={() => {
                prepareDataForExport(data);
              }}
              size="medium"
              sx={{
                height: "40px",
                width: "130px",
                borderRadius: "5px",
              }}>
              {t("conversationPerDay:download")}
            </Button>
          </div>
        </Grid>
        <VictoryChart
          fixLabelOverlap={true}
          responsive={false}
          height={300}
          width={400}
          animate={{
            duration: 500,
            onLoad: { duration: 200 },
          }}
          theme={chartTheme}
          domainPadding={{ x: 30 }}
          padding={70}>
          <VictoryBar
            barRatio={1}
            cornerRadius={{ top: 3 }}
            style={{
              data: {
                fill: "#B474DC",
              },
              labels: {
                fontSize: 5,
                fill: "black",
              },
            }}
            alignment="middle"
            labelComponent={
              <VictoryTooltip
                cornerRadius={1}
                pointerLength={0}
                flyoutStyle={{
                  stroke: "grey",
                  backgroundColor: "red",
                }}
              />
            }
            data={dataGraph}
          />
          <VictoryAxis
            label={t("conversationPerDay:date")}
            style={{
              axisLabel: { padding: 30 },
              tickLabels: { angle: -65, fontSize: 5 },
            }}
          />
          <VictoryAxis
            dependentAxis
            label={t("conversationPerDay:amountOfConversations")}
            style={{
              axisLabel: { padding: 50 },
            }}
          />
        </VictoryChart>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            marginTop: "-30px",
          }}>
          {t("conversationPerDay:totalConversations")}: {total}
        </Typography>
      </Paper>
    </Container>
  );
};

export default ConversationsPerDay;
